import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import Header from './components/Header';
import ScrollToTop from './components/ScrollToTop';
import PageViewTracker from './components/PageViewTracker';
import Footer from './components/Footer';
import Home from './pages/Home';
import OurKitchens from './pages/OurKitchens';
import Members from './pages/Members';
import Story from './pages/Story';
import FAQ from './pages/FAQ';
import ExternalApplyRedirect from './pages/ExternalApplyRedirect';
import FacilityPage from './pages/FacilityPage';
import Referral from './pages/Referral';
import NotFound from './pages/NotFound';
import Privacy from './pages/Privacy';
// THE application form (relaunched 2026-09-02): a faithful copy of v2's form
// that stores to our own intake first, then forwards to v2.
import V2FormReplica from './pages/V2FormReplica';
import V3TypeFirstForm from './pages/V3TypeFirstForm';
import { captureAttribution, captureLanding, installApplyHandoffTracking } from './lib/attribution';
import './App.css';

function App() {
  // First-touch UTM/gclid capture + the apply-handoff conversion listener
  // (COM-771/772) — before any CTA can be clicked.
  useEffect(() => {
    captureAttribution();
    captureLanding();
    installApplyHandoffTracking();
  }, []);
  return (
    <Router>
      <ScrollToTop />
      <div className="App">
        <Header />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/our-kitchens" element={<OurKitchens />} />
            {/* Per-facility ad landing pages (COM-773/774) — the Google Ads
                display URLs promise /kitchens/<slug>; unknown slugs fall back
                to the kitchens overview. */}
            <Route path="/kitchens/:slug" element={<FacilityPage />} />
            <Route path="/members" element={<Members />} />
            <Route path="/story" element={<Story />} />
            <Route path="/faq" element={<FAQ />} />
            {/* The old on-site forms discarded every submission (COM-770) —
                the external application form is canonical; keep the routes
                alive as attribution-preserving redirects. */}
            <Route path="/apply" element={<ExternalApplyRedirect />} />
            <Route path="/intake" element={<ExternalApplyRedirect />} />
            <Route path="/referral" element={<Referral />} />
            {/* The application form (CMSY-21, relaunched 2026-09-02). Every apply
                CTA lands here via APPLY_FORM_URL; ?facilities=N preselects. */}
            <Route path="/application" element={<V2FormReplica />} />
            {/* The unlaunched experiment (CMSY-69). Reachable on purpose so it
                can be looked at and clicked through; noindex, not in the sitemap,
                and linked from nowhere. /application stays the live form. */}
            <Route path="/application-preview" element={<V3TypeFirstForm />} />
            {/* The testers' link from the pre-launch week keeps working. */}
            <Route path="/v2-replica" element={<Navigate to="/application" replace />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            {/* /our-innovations was removed but never redirected, and Google
                still ranks it: 449 impressions at position 10.1 over 90 days,
                zero clicks, because it rendered a blank page. A redirect
                inherits that ranking instead of throwing it away. */}
            <Route path="/our-innovations" element={<Navigate to="/story" replace />} />
            {/* Catch-all. Without this, every unknown path returned HTTP 200
                with an empty <main> — a soft 404 that Google was indexing and
                ranking, including /our-kitchens/%20 at position 4.3. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
        {/* GA4 page_view on every client-side route change (COM-1029).
            Rendered after <main> on purpose: effects commit in tree order,
            so the route's <SEO /> has already set document.title. */}
        <PageViewTracker />
        <SpeedInsights />
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
